import { CallDto, CallUser } from '@act/shared/data-transfer-objects';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { SelectOption } from 'react-components/components/SearchBar';
import { useForm } from 'react-hook-form';
import { membersRequests } from 'redux/requests/members.request';
import { getTeam, userAppSelectors } from 'redux/slices/usersSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { utilityAppSelectors } from 'redux/utils/selectors/utilitySelectors';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';

export interface AddCallerForm {
  guideId: string | null;
  memberId?: string | null;
}

/**
 * Form state, team fetch, and member typeahead for adding a participant to a call.
 *
 * @param call Active call.
 * @param isMemberKnown Whether the call already has a subject member.
 * @param mainConnection Primary connection for phone matching in labels.
 * @param isAddingConnection Whether the add-participant panel is open.
 * @returns Form API and option lists for ActiveCallMenu.
 */
export function useActiveCallAddParticipant(
  call: CallDto,
  isMemberKnown: boolean,
  mainConnection: CallUser | undefined,
  isAddingConnection: boolean
) {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState,
    formState: { isValid },
  } = useForm<AddCallerForm>({
    defaultValues: {
      guideId: null,
      memberId: null,
    },
  });

  const guideId = watch('guideId');
  const memberId = watch('memberId');

  const viewableGuides = userAppSelectors.useSelectCallInviteTeamUsers();
  const selectedMemberFromStore = utilityAppSelectors.selectUserOrMemberById(
    memberId || ''
  );
  const knownMember = utilityAppSelectors.selectUserOrMemberById(
    call?.subjectId || ''
  );

  const [guideOptions, setGuideOptions] = useState<SelectOption[]>([]);
  const [memberOptions, setMemberOptions] = useState<SelectOption[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [selectedMemberOption, setSelectedMemberOption] =
    useState<SelectOption | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const lastLoadedGuideId = useRef<string | null>(null);
  const lastLoadState = useRef('');

  const [typeaheadMembers, isLoadingTypeaheadMembers, getTypeaheadMembers] =
    useApiCall(membersRequests.getPaginatedMembersForTable);

  useEffect(() => {
    if (viewableGuides === null) {
      void dispatch(
        getTeam({ params: { allViewable: true, allCareStaff: true } })
      );
    }
  }, [viewableGuides, dispatch]);

  useEffect(() => {
    if (!viewableGuides) {
      setGuideOptions([]);
      return;
    }

    let options = viewableGuides.map(guide => ({
      label: guide.name,
      value: guide.id,
    }));

    let memberToCheck = null;
    if (isMemberKnown && knownMember) {
      memberToCheck = knownMember;
    } else if (memberId) {
      const selectedMemberFromResults = typeaheadMembers?.users?.find(
        m => m.id === memberId
      );
      memberToCheck = selectedMemberFromResults || selectedMemberFromStore;
    }

    if (memberToCheck?.assignedGuideId) {
      const assignedGuideId = memberToCheck?.assignedGuideId;
      const assignedGuideIndex = options.findIndex(
        opt => opt.value === assignedGuideId
      );

      if (assignedGuideIndex > -1) {
        const assignedGuideObject = viewableGuides.find(
          g => g.id === assignedGuideId
        );
        const assignedGuide = options[assignedGuideIndex];
        const assignedGuideLabel = assignedGuideObject
          ? `${assignedGuideObject.firstName} ${assignedGuideObject.lastName} (assigned guide)`
          : assignedGuide.label;

        options = [
          { ...assignedGuide, label: assignedGuideLabel },
          ...options.filter(opt => opt.value !== assignedGuideId),
        ];
      }
    }

    setGuideOptions(options);
  }, [
    viewableGuides,
    memberId,
    typeaheadMembers,
    selectedMemberFromStore,
    isMemberKnown,
    knownMember,
  ]);

  useEffect(() => {
    if (typeaheadMembers?.users) {
      const optionsMap = new Map<string, SelectOption>();
      const callPhoneNumber = mainConnection?.phoneNumber;

      typeaheadMembers.users.forEach(member => {
        if (!optionsMap.has(member.id)) {
          const baseName =
            member.name || `${member.firstName} ${member.lastName}`;
          const hasPhoneNumber =
            callPhoneNumber &&
            member.phoneNumbers?.some(
              phone => phone.number === callPhoneNumber
            );
          const label = hasPhoneNumber
            ? `${baseName} (assigned to this member)`
            : baseName;

          optionsMap.set(member.id, {
            value: member.id,
            label,
          });
        }
      });

      if (memberId) {
        const selectedInResults = typeaheadMembers.users.find(
          m => m.id === memberId
        );
        if (selectedInResults) {
          const baseName =
            selectedInResults.name ||
            `${selectedInResults.firstName} ${selectedInResults.lastName}`;
          const hasPhoneNumber =
            callPhoneNumber &&
            selectedInResults.phoneNumbers?.some(
              phone => phone.number === callPhoneNumber
            );
          const label = hasPhoneNumber
            ? `${baseName} (assigned to this member)`
            : baseName;
          const freshOption = {
            value: selectedInResults.id,
            label,
          };
          setSelectedMemberOption(freshOption);
          optionsMap.set(memberId, freshOption);
        } else if (selectedMemberOption) {
          if (!optionsMap.has(memberId)) {
            optionsMap.set(memberId, selectedMemberOption);
          }
        }
      }

      setMemberOptions(Array.from(optionsMap.values()));
      setIsLoadingMembers(false);
    } else if (memberId && selectedMemberOption) {
      setMemberOptions([selectedMemberOption]);
    } else {
      setMemberOptions([]);
    }
  }, [
    typeaheadMembers,
    memberId,
    selectedMemberOption,
    mainConnection?.phoneNumber,
  ]);

  useEffect(() => {
    const currentLoadState = `${isAddingConnection}-${isSearchMode}-${lastSearchTerm}-${guideId}`;
    const guideIdChanged = lastLoadedGuideId.current !== guideId;
    const stateChanged = lastLoadState.current !== currentLoadState;

    const shouldLoad =
      isAddingConnection &&
      !isSearchMode &&
      !lastSearchTerm &&
      !isLoadingMembers &&
      (stateChanged || guideIdChanged);

    if (shouldLoad) {
      lastLoadedGuideId.current = guideId;
      lastLoadState.current = currentLoadState;
      setIsLoadingMembers(true);
      getTypeaheadMembers({
        params: {
          'page-start': 0,
          'page-size': 50,
          includeCount: false,
          includeDetails: true,
          ...(guideId ? { guideIds: [guideId] } : {}),
        },
      });
    }
  }, [
    isAddingConnection,
    isSearchMode,
    lastSearchTerm,
    isLoadingMembers,
    guideId,
  ]);

  useEffect(() => {
    if (!isAddingConnection) {
      lastLoadedGuideId.current = null;
      lastLoadState.current = '';
    }
  }, [isAddingConnection]);

  const setTypeaheadSearchTerm = (searchTerm: string) => {
    setLastSearchTerm(searchTerm);
    if (searchTerm && searchTerm.length >= 2) {
      setIsSearchMode(true);
      setIsLoadingMembers(true);
      getTypeaheadMembers({
        params: {
          searchTerm,
          'page-start': 0,
          'page-size': 20,
          includeCount: false,
          includeDetails: true,
          ...(guideId ? { guideIds: [guideId] } : {}),
        },
      });
    } else {
      setIsSearchMode(false);
      setMemberOptions([]);
      setIsLoadingMembers(false);
      lastLoadedGuideId.current = null;
      lastLoadState.current = '';
    }
  };

  const addCallerFormConfig: FormConfig<AddCallerForm> = useMemo(() => {
    return [
      {
        type: 'input',
        name: 'guideId',
        label: 'Guide name',
        input: 'autocomplete',
        trackingName: 'guide-autocomplete',
        sensitive: false,
        props: {
          options: guideOptions,
          getOptionLabel: (option: SelectOption) => option.label || '',
          placeholder: 'Search for a guide...',
          required: false,
        },
      },
    ];
  }, [guideOptions]);

  const resetAddParticipantForm = useCallback(() => {
    reset();
  }, [reset]);

  const onSubmitAddCaller = (
    data: AddCallerForm,
    submitAddGuide: (guideId: string, memberId?: string | null) => void
  ) => {
    if (!data.guideId) {
      toast('Please select a guide', 'error');
      return;
    }
    submitAddGuide(data.guideId, data.memberId);
  };

  return {
    control,
    handleSubmit,
    formState,
    isValid,
    guideId,
    memberId,
    memberOptions,
    isLoadingMembers,
    isLoadingTypeaheadMembers,
    selectedMemberOption,
    setSelectedMemberOption,
    setTypeaheadSearchTerm,
    addCallerFormConfig,
    resetAddParticipantForm,
    onSubmitAddCaller,
  };
}
